import _flowRight from 'lodash/flowRight';
import _uniqueId from 'lodash/uniqueId';
import { normalize } from 'normalizr';
import { getJobs } from '@homeservices/api-kit/proreferral/jobs/v6';

import { JOBS_SCHEMA } from '@/store/modules/entities';
import { withTakeLatest } from '@/utils/cancel-token';
import { toSnakeCase } from '@/utils/to-snake-case';
import { withRequestLifecycle, REQUEST_LIFECYCLE } from '@/utils/request-lifecycle';

const MUTATION_KEY = 'patchJobs';

const emptyArray = [];

const initialPartialState = {
  status: REQUEST_LIFECYCLE.UNINITIALIZED,
  error: null,
  result: emptyArray,
  count: 0,
  total: 0,
  hasMore: false,
};

export default {
  namespaced: true,
  state: {
    jobsKey: _uniqueId(),
  },
  getters: {
    jobsKey: state => state.jobsKey,
    getJobsMetaByStatusAlias: state => (jobsStatusAlias) => {
      const { result, ...meta } = state[jobsStatusAlias] || initialPartialState;

      return meta;
    },
    getRawJobsByStatusAlias: state => jobsStatusAlias =>
      (state[jobsStatusAlias] || initialPartialState).result,
  },
  actions: {
    fetchJobs: _flowRight(
      withTakeLatest('jobsStatusAlias'),
      withRequestLifecycle(MUTATION_KEY),
      () => async (
        { dispatch, rootGetters, rootGetters: { 'user/userId': userId, 'user/isPro': isPro } },
        {
          injected: { cancelToken, performFulfillment, performSideEffect },
          includeAppointments,
          params = {},
        }
      ) => {
        await dispatch('account/pro/fetch', null, { root: true });

        const [serviceProviderId] = rootGetters['account/pro/serviceProvidersIds'];

        const {
          data: { results, total },
        } = await getJobs(
          { [isPro ? 'pro' : 'consumer']: userId, ...toSnakeCase(params) },
          cancelToken
        );
        const { result, entities } = normalize(results, [JOBS_SCHEMA]);

        const jobHashs = results.map(r => (r.job_hash));


        if (includeAppointments) {
          performSideEffect(() =>
            dispatch(
              'dashboard/appointments/fetchAppointments',
              {
                bucketName: 'jobs',
                params: { order_hash: jobHashs.join(','), service_provider_id: serviceProviderId, size: 100 },
              },
              { root: true }
            )
          );
        }

        return performFulfillment({
          total,
          result,
          entities,
        });
      }
    )(),
    resetJobs: ({ commit }, { jobsStatusAlias }) =>
      commit('resetJobs', { meta: { jobsStatusAlias } }),
  },
  mutations: {
    [MUTATION_KEY] (state, payload) {
      const { jobsStatusAlias } = payload.meta;
      const partialState = state[jobsStatusAlias] || initialPartialState;
      const result = [...new Set([...partialState.result, ...(payload.result || emptyArray)])];

      state[jobsStatusAlias] = {
        ...partialState,
        ...payload,
        result,
        count: result.length,
        hasMore: result.length < payload.total,
      };

      state.jobsKey = _uniqueId();
    },
    resetJobs (state, { meta }) {
      const { jobsStatusAlias } = meta;

      state[jobsStatusAlias] = initialPartialState;
      state[jobsStatusAlias].meta = meta;

      state.jobsKey = _uniqueId();
    },
    transitionJob (state, { meta }) {
      const { from, to, jobId } = meta;
      const fromPartialState = state[from] || initialPartialState;
      const fromResult = fromPartialState.result.filter(fromJobId => fromJobId !== jobId);
      const fromNewTotal = Math.max(fromPartialState.total - 1, 0);
      const toPartialState = state[to] || initialPartialState;
      const toResult = [...new Set([jobId, ...toPartialState.result])];
      const toNewTotal = toPartialState.total + 1;

      state[from] = {
        ...fromPartialState,
        meta,
        status: 'mutated',
        result: fromResult,
        count: fromResult.length,
        total: fromNewTotal,
        hasMore: fromResult.length < fromNewTotal,
      };

      if (to) {
        state[to] = {
          ...toPartialState,
          meta,
          status: 'mutated',
          result: toResult,
          count: toResult.length,
          total: toNewTotal,
          hasMore: toResult.length < toNewTotal,
        };
      }

      state.jobsKey = _uniqueId();
    },
  },
};
